import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import MaskedLines from "@/components/landing/MaskedLines";

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSent(true);
    toast.success("Welcome to the journal. We'll write softly.");
    setEmail("");
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <section
      id="newsletter"
      data-testid="newsletter-section"
      className="relative py-32 md:py-44 bg-[#0a0f1d] overflow-hidden"
    >
      <Toaster
        theme="dark"
        position="bottom-center"
        toastOptions={{
          style: {
            background: "#0a0f1d",
            border: "1px solid #D4AF37",
            color: "#fff",
          },
        }}
      />

      {/* Decorative giant initials */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-10 -right-20 md:-right-10 select-none"
      >
        <span className="font-serif-display text-[28vw] md:text-[18vw] leading-none italic text-white/[0.04]">
          HB
        </span>
      </div>

      <div className="relative max-w-[1100px] mx-auto px-6 md:px-12 text-center">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="label-overline"
        >
          05 · The Journal
        </motion.span>

        <MaskedLines
          as="h3"
          data-testid="newsletter-headline"
          delay={0.1}
          className="mt-6 font-serif-display text-white text-4xl md:text-6xl lg:text-7xl font-light leading-[1.05]"
          lines={[
            "A letter from the",
            <span key="l2">
              <em className="italic text-[#D4AF37]">mountains</em>, monthly.
            </span>,
          ]}
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.2 }}
          className="mt-8 text-white/70 max-w-xl mx-auto leading-relaxed"
        >
          Drop dates, behind-the-loom photographs, and the occasional
          long-form essay from the atelier. No marketing noise.
        </motion.p>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.35 }}
          onSubmit={onSubmit}
          data-testid="newsletter-form"
          className="mt-12 max-w-xl mx-auto flex flex-col sm:flex-row items-stretch gap-3"
        >
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            data-testid="newsletter-input"
            className="flex-1 bg-transparent border border-white/15 px-5 py-4 text-white placeholder:text-white/35 focus:outline-none focus:border-[#D4AF37] transition-colors tracking-wider"
          />
          <button
            type="submit"
            data-testid="newsletter-submit"
            disabled={sent}
            className="group relative px-8 py-4 border border-[#D4AF37] text-[#D4AF37] tracking-[0.25em] text-xs uppercase overflow-hidden transition-colors duration-500 hover:text-[#0a0f1d] disabled:opacity-50"
          >
            <span className="absolute inset-0 bg-[#D4AF37] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-[cubic-bezier(0.77,0,0.18,1)]" />
            <span className="relative z-10">
              {sent ? "Sent ✓" : "Subscribe"}
            </span>
          </button>
        </motion.form>

        <p className="mt-6 text-[11px] tracking-[0.25em] uppercase text-white/40">
          One letter · Twelve a year · Zero spam
        </p>
      </div>
    </section>
  );
}
